import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { SUBSCRIPTION_STATUS_CHIP } from '@/features/customers';
import { useCursorSelect } from '@/hooks/use-cursor-select';

import { listReportCustomers } from '../../api';
import { reportKeys } from '../../hooks';
import type { ReportOption } from '../../types';
import { toDefaults, toFilters } from '../filters';
import { PRODUCT_TYPE_LABEL } from '../format';
import { productsReportFiltersSchema } from '../schemas';
import type {
  ProductsReportFilterFormValues,
  ProductsReportListFilters,
  ProductsReportProductType,
} from '../types';

// Both option lists are derived from the shipped label maps rather than re-listed here, so a label
// or token can never drift between the table cell and the filter that selects it.
const PRODUCT_TYPE_OPTIONS = (Object.keys(PRODUCT_TYPE_LABEL) as ProductsReportProductType[]).map(
  (value) => ({ value, label: PRODUCT_TYPE_LABEL[value] }),
);

/** Exactly two options, and deliberately no third "non-subscribable" one: selecting ANY status
 *  excludes the hyphen rows, which are reachable only with the filter cleared (BA-confirmed).
 *
 *  Derived from `SUBSCRIPTION_STATUS_CHIP` — the SAME single reference the Products table cell and
 *  `customerSubscriptionStatusSchema` already use. The Active/Expired spelling is provisional
 *  pending BA on 112361 (comment 20841648) and the deployed backend types say `active | inactive`;
 *  keeping one reference is what makes that a swap rather than a hunt. Do not inline the tokens. */
const SUBSCRIBED_STATUS_OPTIONS = (
  Object.keys(SUBSCRIPTION_STATUS_CHIP) as (keyof typeof SUBSCRIPTION_STATUS_CHIP)[]
).map((value) => ({ value, label: SUBSCRIPTION_STATUS_CHIP[value].label }));

const customerToOption = (c: ReportOption) => ({ value: c.id, label: c.name });

export type ProductsReportFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: ProductsReportListFilters;
  onApply: (filters: ProductsReportListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<ProductsReportFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const form = useForm<ProductsReportFilterFormValues>({
    resolver: zodResolver(productsReportFiltersSchema),
    defaultValues: toDefaults(value),
    mode: 'onBlur',
  });

  // Cursor-paginated, server-searched typeahead: scroll the popup to load more; a new term resets
  // to the first page (the cursor is only valid for the term it was issued under). Scoped to
  // customers who own at least one product via `hasProduct=true`, inside `listReportCustomers`.
  const customerSelect = useCursorSelect({
    queryKey: reportKeys.metaCustomers,
    fetchPage: listReportCustomers,
    toOption: customerToOption,
  });

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customers"
        placeholder="Select"
        showSearch={customerSelect.showSearch}
        loading={customerSelect.loading}
        onPopupScroll={customerSelect.onPopupScroll}
        options={customerSelect.options}
      />

      <MultiSelectField
        name="productType"
        control={form.control}
        label="Product Type"
        placeholder="Select"
        options={PRODUCT_TYPE_OPTIONS}
      />

      <MultiSelectField
        name="subscriptionStatus"
        control={form.control}
        label="Subscribed Status"
        placeholder="Select"
        options={SUBSCRIBED_STATUS_OPTIONS}
      />
    </Drawer>
  );
}

/** Filter panel for the Products report (Figma 32:190323). Unmounts on close and remounts fresh on
 *  open, so the draft always re-seeds from the last applied `value` — which is both how Cancel/close
 *  discards an edit and how reopening shows the current selections. */
export function ProductsReportFilterDrawer({ open, ...rest }: ProductsReportFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  return <FilterBody {...rest} />;
}
